import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocomplete } from '@angular/material/autocomplete';
import { catchError, debounceTime, switchMap, tap } from 'rxjs/operators';
import { of, Subscription } from 'rxjs';
import { CompanyService } from 'src/app/private/services/company.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { applyCenterScopeToWhere } from 'src/app/utils/tenant-scope.util';

@Component({
  selector: 'app-auto-complete-search',
  templateUrl: './auto-complete-search.component.html',
  styleUrls: ['./auto-complete-search.component.scss']
})
export class AutoCompleteSearchComponent implements OnInit, AfterViewInit, OnDestroy {
  searchControl = new FormControl('');
  @Input() label: string = 'Search Parent Account';
  @Input() hideLabel = false;
  @Input() placeholder = 'Search';
  options: any[] = [];
  page = 0;
  readonly pageSize = 100;
  totalCount = 0;
  isLoading = false;
  selectedId: any = null;
  @Input() selectedName: any = null;
  @Input() account: any;
  @Input() formAction: string = 'Add';
  @Input() type: any;

  @Input() applyStyle: boolean = false;
  /** Match lead/contact modal form-control height and styling */
  @Input() modalField = false;
  @Output() selectedData = new EventEmitter<any>();

  @ViewChild(MatAutocomplete) autocomplete!: MatAutocomplete;

  private panelEl: HTMLElement | null = null;
  private readonly onPanelScroll = (event: Event) => this.handlePanelScroll(event);
  private openedSub?: Subscription;
  private searchSub?: Subscription;

  constructor(
    private api: CompanyService,
    private localStorage: LocalStorageService
  ) { }

  ngOnInit() {
    if (this.formAction == 'Update') {
      if (this.type == 'account') {
        this.searchControl.setValue(this.account.ParentAccount?.account_name);
        this.selectedId = this.account?.parent_account;
      } else {
        this.searchControl.setValue(this.account?.account_name);
        this.selectedId = this.account?.account_id;
      }
    }

    this.loadAccounts('');

    this.searchSub = this.searchControl.valueChanges
      .pipe(
        debounceTime(400),
        tap(() => {
          this.page = 0;
          this.options = [];
          this.totalCount = 0;
        }),
        switchMap(value => {
          const query = typeof value === 'string' ? value.trim() : '';
          return this.fetchData(query);
        })
      )
      .subscribe(data => this.applyResponse(data, false));
  }

  ngAfterViewInit() {
    this.openedSub = this.autocomplete?.opened?.subscribe(() => {
      setTimeout(() => this.attachPanelScrollListener(), 0);
    });
  }

  ngOnDestroy() {
    this.detachPanelScrollListener();
    this.openedSub?.unsubscribe();
    this.searchSub?.unsubscribe();
  }

  private loadAccounts(query: string) {
    this.page = 0;
    this.fetchData(query).subscribe(data => this.applyResponse(data, false));
  }

  fetchData(query: any) {
    this.isLoading = true;

    const param: any = {
      page: this.page * this.pageSize,
      limit: this.pageSize,
      filter: {},
      where: applyCenterScopeToWhere(
        { client_id: this.localStorage.getClientId() },
        this.localStorage
      ),
      order: {
        col: '"account"."account_name"',
        dir: 'ASC'
      }
    };

    const search = typeof query === 'string' ? query.trim() : '';
    if (search) {
      // Backend expects staticFilter / dynamicFilter — not flat field keys
      param.filter = {
        staticFilter: { account_name: search }
      };
    }

    return this.api.getAllCompany(param).pipe(
      tap(() => (this.isLoading = false)),
      catchError((error) => {
        this.isLoading = false;
        console.error('Error fetching data:', error);
        return of({ rows: [], count: 0 });
      })
    );
  }

  private applyResponse(data: any, append: boolean) {
    const rows = Array.isArray(data?.rows) ? data.rows : [];
    this.totalCount = Number(data?.count ?? 0);
    this.options = append ? [...this.options, ...rows] : rows;
    this.isLoading = false;
  }

  private attachPanelScrollListener() {
    this.detachPanelScrollListener();
    this.panelEl = document.querySelector('.mat-mdc-autocomplete-panel, .mat-autocomplete-panel');
    this.panelEl?.addEventListener('scroll', this.onPanelScroll);
  }

  private detachPanelScrollListener() {
    this.panelEl?.removeEventListener('scroll', this.onPanelScroll);
    this.panelEl = null;
  }

  private handlePanelScroll(event: Event) {
    const element = event.target as HTMLElement;
    const nearBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 24;
    const hasMore = this.options.length < this.totalCount;

    if (!nearBottom || this.isLoading || !hasMore) {
      return;
    }

    this.page++;
    const query = typeof this.searchControl.value === 'string' ? this.searchControl.value.trim() : '';
    this.fetchData(query).subscribe(data => this.applyResponse(data, true));
  }

  onSelectionChange(event: any) {
    this.selectedId = event?.account_id;
    this.selectedName = event?.account_name;
    this.selectedData.emit(event);
  }
}
